import { useState, useCallback, useMemo, useEffect } from "react"
import PropTypes from "prop-types"
import { useDispatch } from "react-redux"
import classNames from "classnames"
import { some, filter, orderBy, includes, isEmpty, noop } from "lodash"
import { Delete, Add } from "@mui/icons-material"
import { Autocomplete, Button, TextField, IconButton, Tooltip, Box, Popover } from "@mui/material"
import { DROPDOWN_OPTION } from "helpers/shapes"
import SecuritiesDialogSelector from "components/add-filter/securities-dialog-selector"
import { convertUnifiedSearchItemToFilterItem, compareSecurity } from "components/add-filter/helpers"
import { EMPTY_FILTER } from "components/add-filter/helpers"
import { FilterOption } from "components/add-filter/filter-option"
import SecuritiesSearch from "components/add-filter/securities-search"
import { Creators as UnifiedSearchCreators } from "modules/unified-search/model"

export default function AddUniverse({
  updateFilters,
  portfoliosList,
  watchlistsList,
  etfsList,
  swapsList,
  indexesList,
  account,
  modelId,
  existingFilters,
  filterDisabled = noop,
}) {
  const dispatch = useDispatch()
  const [filters, setFilters] = useState(() => existingFilters.length > 0 ? [...existingFilters] : EMPTY_FILTER)
  const [activeSearchIndex, setActiveSearchIndex] = useState(null)
  const [anchorElement, setAnchorElement] = useState(null)

  const handleSearchSecurities = useCallback((index) => (anchorElement = null) => {
    setAnchorElement(anchorElement)
    setActiveSearchIndex(index)
  }, [setAnchorElement, setActiveSearchIndex])

  const handleCloseSecuritiesSearch = useCallback(() => {
    setActiveSearchIndex(null)
    setAnchorElement(null)
    dispatch(UnifiedSearchCreators.close())
  }, [dispatch])

  const handleSetFilterOn = (index, filterOn) => {
    setFilters(currentFilters => currentFilters.map((filter, i) => {
      if (i !== index) return filter
      const updatedFilterOn = {
        ...filterOn,
        type: filterOn.value === "securities" ? "securities" : "universe",
      }
      return { ...filter, filterOn: updatedFilterOn, values: [] }
    }))
  }

  const handleSetOperator = (index, operator) => {
    setFilters(currentFilters => currentFilters.map((filter, i) =>
      i === index
        ? { ...filter, operator }
        : filter,
    ))
  }

  const handleSetValues = useCallback((index, ...newValues) => {
    setFilters(currentFilters => currentFilters.map((filter, i) => {
      if (i !== index) return filter
      return { ...filter, values: newValues }
    }))
  }, [])

  const handleAppendFilterValue = useCallback((index, item) => {
    setFilters(currentFilters => currentFilters.map((filter, i) =>
      i === index
        ? { ...filter, values: [...filter.values, item] }
        : filter,
    ))
  }, [])

  const deselectSecurity = useCallback((item) => {
    const { assetClass, exchange, ticker } = item
    setFilters(currentFilters => currentFilters.map((filterObj, i) => i === activeSearchIndex
      ? { ...filterObj, values: filter(filterObj.values, v => {
        if (assetClass === "index") {
          return v.indexId !== ticker
        } else {
          return !(v.ticker === ticker && v.mic === exchange)
        }
      }),
      } : filterObj,
    ))
  }, [activeSearchIndex])

  const deselectIndex = useCallback((id) => {
    setFilters(currentFilters => currentFilters.map((filterObj, i) =>
      i === activeSearchIndex
        ? { ...filterObj, values: filter(filterObj.values, v => v.indexId !== id) }
        : filterObj,
    ))
  }, [activeSearchIndex])

  const disableSearchResult = useCallback(() => (searchResult) => {
    if (!includes(["security", "index"], searchResult.type)) {
      return true
    }
    return false
  }, [])

  const handleSecuritySelected = useCallback((index) => (searchResult) => {
    const currentValues = filters[index]?.values || []
    return some(currentValues, selectedSecurity => {
      if (searchResult.type === "index") {
        return selectedSecurity.indexId === searchResult.id
      } else {
        const { assetClass, ticker, mic } = searchResult
        if (assetClass === "index") {
          return selectedSecurity.indexId === ticker
        } else {
          return selectedSecurity.ticker === ticker && selectedSecurity.mic === mic
        }
      }
    })
  }, [filters])

  const itemsActions = useMemo(() => {
    if (activeSearchIndex === null) return {}

    return {
      index: {
        noDispatch: true,
        mainAction: item => () => {
          const { type, id, name } = item
          const currentValues = filters[activeSearchIndex]?.values || []
          const isSelected = some(currentValues, selectedItem => selectedItem.indexId === id)
          if (isSelected) {
            deselectIndex(id)
          } else {
            handleAppendFilterValue(activeSearchIndex, { id, assetClass: type, indexId: id, value: id, label: name })
          }
        },
      },
      security: {
        noDispatch: true,
        mainAction: item => () => {
          const currentValues = filters[activeSearchIndex]?.values || []
          const security = convertUnifiedSearchItemToFilterItem(item)
          const isSelected = some(currentValues, selectedSecurity => compareSecurity(selectedSecurity, security))
          if (isSelected) {
            deselectSecurity(item)
          } else {
            handleAppendFilterValue(activeSearchIndex, security)
          }
        },
      },
    }
  }, [activeSearchIndex, handleAppendFilterValue, deselectSecurity, filters, deselectIndex])

  const handleRemoveFilter = (index) => {
    const noRemainingAppliedFilters = filters[index].isExisting && filters.length === 1
    if (noRemainingAppliedFilters) {
      setFilters(EMPTY_FILTER)
    } else {
      setFilters(currentFilters => {
        const newFilters = currentFilters.filter((_, i) => i !== index)
        if (newFilters.length === 0) {
          return EMPTY_FILTER
        }
        return newFilters
      })
    }
  }

  const handleClearSecurities = useCallback((index) => {
    setFilters(currentFilters => currentFilters.map((filter, i) =>
      i === index
        ? { ...filter, values: [] }
        : filter,
    ))
  }, [])

  const handleDeleteSecurity = useCallback((index, item) => {
    setFilters(currentFilters => currentFilters.map((filterObj, i) =>
      i === index
        ? { ...filterObj, values: filter(filterObj.values, v => v.assetClass === "index" ? v.indexId !== item.indexId : v.ticker !== item.ticker || v.mic !== item.mic) }
        : filterObj,
    ))
  }, [])

  const handleAddAnotherFilter = () => {
    setFilters(currentFilters => [...currentFilters, ...EMPTY_FILTER])
  }

  useEffect(() => {
    updateFilters(filters)
  }, [filters, updateFilters])

  const options = useMemo(() => {
    const options = [
      { label: "Portfolio", value: "portfolio" },
      { label: "Securities", value: "securities" },
    ]

    if (watchlistsList && watchlistsList.length) {
      options.push({ label: "Watchlist", value: "watchlist" })
    }

    if (etfsList && etfsList.length) {
      options.push({ label: "ETF", value: "etf" })
    }

    if (swapsList && swapsList.length) {
      options.push({ label: "Basket", value: "swap" })
    }

    if (indexesList && indexesList.length) {
      options.push({ label: "Index", value: "index" })
    }

    return orderBy(options, "label")
  }, [watchlistsList, etfsList, swapsList, indexesList])

  const renderUniverseList = useCallback((index) => {
    const filter = filters[index]
    const filterOn = filter?.filterOn
    const values = filter?.values || []

    if (!filterOn) {
      return null
    }

    let options, placeholder, renderOption, getOptionLabel = (option) => option.label ?? option

    if (filterOn.value === "securities") {
      return <SecuritiesDialogSelector
        values={values}
        handleDelete={(item) => handleDeleteSecurity(index, item)}
        searchSecurities={handleSearchSecurities(index)}
        clearSecurities={() => handleClearSecurities(index)}
        disableSearchResult={disableSearchResult(activeSearchIndex)}
        securitySelected={handleSecuritySelected(activeSearchIndex)}
      />
    }

    const value = values[0] || null

    switch (filterOn.value) {
      case "portfolio":
        options = portfoliosList
        placeholder = "Select a portfolio..."
        break
      case "watchlist":
        options = watchlistsList
        placeholder = "Select a watchlist..."
        break
      case "swap":
        options = swapsList
        placeholder = "Select a basket..."
        break
      case "etf":
        options = etfsList
        placeholder = "Select an ETF..."
        getOptionLabel = (option) => {
          const selectedOption = options.find(o => o.value === option.value)
          return `${selectedOption.ticker} ${selectedOption.label}`
        }
        renderOption = ({ key, ...props }, option) => {
          return <div key={key} {...props} className="etf-option">
            <span className="ticker">{option.ticker}</span>
            <span className="label">{option.label}</span>
            <span className="mic">{option.mic}</span>
          </div>
        }
        break
      case "index":
        options = indexesList
        placeholder = "Select an index..."
        break
    }

    if (options) {
      return <Autocomplete
        options={options}
        value={value}
        blurOnSelect
        clearOnBlur
        disableClearable
        disabled={filter.isUnsupported || filterDisabled(filter)}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={(option, value) => option?.value === value?.value}
        placeholder={placeholder}
        onChange={(_, value) => handleSetValues(index, value)}
        renderOption={renderOption}
        renderInput={(params) => <TextField
          {...params}
          variant="standard"
        />}
      />
    }

    return null
  }, [filters, handleSetValues, handleDeleteSecurity, handleSearchSecurities, etfsList, swapsList, indexesList, watchlistsList, portfoliosList, activeSearchIndex, disableSearchResult, handleSecuritySelected, handleClearSecurities, filterDisabled])

  const isFilterValid = useCallback((filter) => {
    if (!filter.operator) return false
    if (!filter.filterOn) return false
    if (!filter.filterOn.value) return false
    if (isEmpty(filter.values)) return false
    return true
  }, [])

  const isAddButtonDisabled = useMemo(() => {
    if (filters.length === 0) return false
    const lastFilter = filters[filters.length - 1]
    return !isFilterValid(lastFilter)
  }, [filters, isFilterValid])

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <div className="filter-header">
        Universe
      </div>
      <div className="universe-filters filter-content-wrapper">
        <div className="universe-filters filter-content">
          <Box flexGrow={1} display="flex" flexDirection="column" gap={1}>
            {filters.map((filter, index) => {
              const isUnsupported = filter.isUnsupported
              const filterRow = (
                <Box
                  display="flex"
                  gap={1.5}
                  key={`${index}-${filter.id}`}
                  className={classNames("universe-filter-content-group", { "unsupported-filter": isUnsupported })}
                >
                  <Box width={200} className="filter-operator">
                    <Autocomplete
                      options={[
                        { label: "is in", value: "in" },
                        { label: "is not in", value: "notIn" },
                      ]}
                      value={filter.operator}
                      blurOnSelect
                      clearOnBlur
                      disableClearable
                      disabled={isUnsupported || filterDisabled(filter)}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      onChange={(_, operator) => handleSetOperator(index, operator)}
                      renderInput={(params) => <TextField
                        {...params}
                        variant="standard"
                        fullWidth
                      />}
                      className="filter-control"
                    />
                  </Box>
                  <Box width={200} className="filter-on">
                    <Autocomplete
                      options={options}
                      value={filter.filterOn}
                      renderOption={(props, option) => <FilterOption {...props} option={option} disabled={option.isUnsupported} />}
                      blurOnSelect
                      clearOnBlur
                      disableClearable
                      disabled={isUnsupported || filterDisabled(filter)}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      groupBy={option => option.groupLabel}
                      getOptionKey={(option) => `${option.groupLabel} - ${option.value}`}
                      onChange={(_, filterOn) => handleSetFilterOn(index, filterOn)}
                      getOptionDisabled={(option) => option.isUnsupported}
                      renderInput={(params) => <TextField
                        {...params}
                        variant="standard"
                        placeholder="Select a universe type..."
                        fullWidth
                      />}
                      className="filter-control filter-on"
                      slotProps={{
                        paper: {
                          sx: {
                            minWidth: "fit-content",
                            width: "auto",
                          },
                        },
                      }}
                    />
                  </Box>
                  <Box width={200} className="filter-values">
                    {renderUniverseList(index)}
                  </Box>
                  <div>
                    <IconButton disabled={filterDisabled(filter)} onClick={() => handleRemoveFilter(index)} className="remove-universe-filter-btn">
                      <Delete />
                    </IconButton>
                  </div>
                </Box>
              )
              return isUnsupported ? (
                <Tooltip key={`${index}-${filter.id}`} title="The filter could not be found" arrow>
                  {filterRow}
                </Tooltip>
              ) : filterRow
            })}
          </Box>
        </div>
      </div>
      <div className={classNames("add-another-wrapper", { hidden: isAddButtonDisabled })}>
        <Button
          onClick={handleAddAnotherFilter}
          variant="text"
          color="primary"
          startIcon={<Add />}
          className="add-another-universe-btn"
        >
          Add another universe
        </Button>
      </div>
      <Popover
        className="securities-search-popover"
        onClose={handleCloseSecuritiesSearch}
        open={Boolean(anchorElement)}
        anchorEl={anchorElement}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 0,
              minHeight: 0,
            },
          },
        }}
      >
        <div className={classNames("securities-search-wrapper", { hidden: !anchorElement })}>
          <SecuritiesSearch
            account={account}
            modelId={modelId}
            itemsActions={itemsActions}
            disableSearchResult={disableSearchResult(activeSearchIndex)}
            securitySelected={handleSecuritySelected(activeSearchIndex)}
          />
        </div>
      </Popover>
    </Box>
  )
}

AddUniverse.propTypes = {
  updateFilters: PropTypes.func.isRequired,
  portfoliosList: PropTypes.arrayOf(DROPDOWN_OPTION).isRequired,
  watchlistsList: PropTypes.arrayOf(DROPDOWN_OPTION).isRequired,
  etfsList: PropTypes.arrayOf(DROPDOWN_OPTION).isRequired,
  swapsList: PropTypes.arrayOf(DROPDOWN_OPTION).isRequired,
  indexesList: PropTypes.arrayOf(DROPDOWN_OPTION).isRequired,
  account: PropTypes.string.isRequired,
  modelId: PropTypes.string.isRequired,
  existingFilters: PropTypes.arrayOf(PropTypes.object).isRequired,
  filterDisabled: PropTypes.func,
}
